// Artifact (arc 101 slice 08 — "BYO S3", doc 08). Agents produce artifacts —
// screenshots (UI-Debugger evidence), diagrams, datasets, build outputs — and
// need to share them with the user or with other agents. Accounts bring their
// OWN S3-compatible bucket (AWS / R2 / B2 / Hetzner / MinIO); we orchestrate and
// only ever store METADATA — this row — never the customer's bytes.
//
// This is the DOMAIN half: the visibility vocabulary, the expiry model (the pure
// rules that turn a request + the account default + the bucket kind into a
// concrete `expires_at`), and the normalized row shape. Pure — no I/O (domain
// imports nothing internal). The object key builder + presign live in
// `@developerz/storage`; the table + queries in `@developerz/db`; the verbs in
// `@developerz/mcp` + `apps/api`.

import { z } from 'zod';
import { AccountIdSchema } from '../value-objects/ids';

// ─── visibility ──────────────────────────────────────────────────────────────

/**
 * Who may reach an artifact. `run` (the default) scopes it to the producing run
 * — sibling agents on the same run + the operator. `account` widens it to any of
 * the account's agents/users. `public-link` is the outward-facing state minted
 * ONLY by `artifact_share` (an audited action): the artifact may be handed to a
 * user via a link. Text + Zod-at-read in the DB (D3 no-pgEnum stance, same as
 * `runners.status`).
 */
export const ArtifactVisibility = {
  /** Scoped to the producing run — sibling agents + the operator. The default. */
  Run: 'run',
  /** Any of the account's agents / users. */
  Account: 'account',
  /** Shared outward via a link (minted only by the audited `artifact_share`). */
  PublicLink: 'public-link',
} as const;
export const ArtifactVisibilitySchema = z.enum(ArtifactVisibility);
export type ArtifactVisibility = z.infer<typeof ArtifactVisibilitySchema>;

/**
 * The visibilities a caller may request AT UPLOAD (`artifact_put`). Never
 * `public-link`: going outward is `artifact_share`'s job — a separate, audited
 * step — so a put can't silently publish. Widening happens later via share.
 */
export const ArtifactPutVisibilitySchema = z.enum([
  ArtifactVisibility.Run,
  ArtifactVisibility.Account,
]);

// ─── expiry model ────────────────────────────────────────────────────────────

/** Default artifact lifetime — 7 days (doc 08 "default 7d"). */
export const DEFAULT_ARTIFACT_TTL_SECONDS = 7 * 24 * 60 * 60;

/**
 * The HARD ceiling on the platform-provided R2 bucket (no BYO bucket connected).
 * The free tier keeps working with storage COGS ~nil, so platform artifacts get
 * a fixed 7-day life with NO override — a longer / `never` request is silently
 * capped to this (doc 08 "no-BYO → platform R2 hard 7d expiry (no override)").
 */
export const PLATFORM_ARTIFACT_MAX_TTL_SECONDS = 7 * 24 * 60 * 60;

/** The literal that turns off expiry — an artifact that lives until deleted. */
export const ARTIFACT_NEVER_EXPIRES = 'never' as const;

/** A safety ceiling on a custom TTL (~10 years) so a bogus huge value can't overflow the Date. */
export const ARTIFACT_MAX_TTL_SECONDS = 10 * 365 * 24 * 60 * 60;

/**
 * A requested / configured expiry: either the literal `'never'` (no expiry) or a
 * positive whole number of SECONDS. Used for the per-item `expires_in` override
 * and the account-level default (stored on the S3 integration config).
 */
export const ArtifactExpiresInSchema = z.union([
  z.literal(ARTIFACT_NEVER_EXPIRES),
  z.number().int().positive().max(ARTIFACT_MAX_TTL_SECONDS),
]);
export type ArtifactExpiresIn = z.infer<typeof ArtifactExpiresInSchema>;

/** Inputs to {@link resolveArtifactExpiresAt} — the whole expiry decision in one struct. */
export type ResolveArtifactExpiryInput = {
  /** Wall clock — the base the TTL is added to (injected for deterministic tests). */
  now: Date;
  /**
   * True when the target is the PLATFORM R2 default (no BYO bucket connected).
   * Forces the hard 7-day cap and ignores every override (the free-tier fence).
   */
  platformDefaultBucket: boolean;
  /** Per-item override from `artifact_put` (`expires_in`); undefined = not supplied. */
  requested?: ArtifactExpiresIn;
  /** Account-level default from the S3 integration config; undefined/null = none set. */
  accountDefault?: ArtifactExpiresIn | null;
};

/**
 * Resolve the concrete `expires_at` for a new artifact, or `null` for "never".
 * Precedence (doc 08 "Expiry"):
 *   - PLATFORM R2 (no BYO): HARD {@link PLATFORM_ARTIFACT_MAX_TTL_SECONDS}, no
 *     override — the per-item request AND the account default are both ignored,
 *     so the free tier can never pin storage forever.
 *   - BYO bucket: per-item `requested` wins → else the `accountDefault` → else
 *     {@link DEFAULT_ARTIFACT_TTL_SECONDS}. `'never'` at either tier ⇒ `null`.
 * Pure + total: no throw, deterministic given `now`.
 */
export function resolveArtifactExpiresAt(input: ResolveArtifactExpiryInput): Date | null {
  if (input.platformDefaultBucket) {
    return addSeconds(input.now, PLATFORM_ARTIFACT_MAX_TTL_SECONDS);
  }
  const effective = input.requested ?? input.accountDefault ?? DEFAULT_ARTIFACT_TTL_SECONDS;
  if (effective === ARTIFACT_NEVER_EXPIRES) return null;
  return addSeconds(input.now, effective);
}

/**
 * True when a caller's per-item `expires_in` will be IGNORED because there is no
 * BYO bucket — the platform tier caps everything at 7d. The verb surfaces this so
 * the caller learns their custom expiry didn't take (honest, not silent).
 */
export function artifactExpiryCapped(input: {
  platformDefaultBucket: boolean;
  requested?: ArtifactExpiresIn;
}): boolean {
  return input.platformDefaultBucket && input.requested !== undefined;
}

function addSeconds(base: Date, seconds: number): Date {
  return new Date(base.getTime() + seconds * 1000);
}

// ─── created-by ──────────────────────────────────────────────────────────────

/**
 * The `created_by` descriptor — WHO produced the artifact (doc 08 "created_by
 * (run or user)"). A run-scoped upload records `run:<runId>` so it threads into
 * that run's timeline; a bare user/operator upload records `user:<tokenId>`. A
 * plain opaque label, never parsed for auth — the account scope is the fence.
 */
export function artifactCreator(input: { runId?: string | null; tokenId: string }): string {
  return input.runId ? `run:${input.runId}` : `user:${input.tokenId}`;
}

// ─── the durable row ─────────────────────────────────────────────────────────

/** A 64-char lowercase-hex SHA-256 digest — validated at the verb boundary when supplied. */
export const ArtifactSha256Schema = z
  .string()
  .regex(/^[0-9a-f]{64}$/, 'sha256 must be 64 lowercase hex chars');

/**
 * A normalized `artifacts` row. `key` is the object key in the account's bucket
 * — server-side only; it NEVER leaves the control plane (the presigned url is the
 * one path to the bytes, mirroring `ci_jobs.log_ref`). `repoId`/`runId`/`taskId`
 * are optional cross-links for `artifact_list` filtering. `size`/`sha256` are
 * null until the box has uploaded the bytes (the put mints a presigned URL; the
 * client may declare them, or they land on a later confirm). `expiresAt` null =
 * never (the artifact lives until an explicit delete or the account offboards).
 */
export const ArtifactSchema = z.object({
  /** Internal uuid PK — also the unguessable component of the object key. */
  id: z.string().uuid(),
  accountId: AccountIdSchema,
  /** Repository this artifact belongs to (cross-link for listing); null when unscoped. */
  repoId: z.string().nullable(),
  /** Producing run id (`fleet.evt.<run_id>`); null when not run-scoped. */
  runId: z.string().nullable(),
  /** Producing task id (`tsk_…`); null when not task-scoped. */
  taskId: z.string().nullable(),
  /** Object key in the bucket — SERVER-SIDE ONLY, never surfaced to a client. */
  key: z.string().min(1),
  contentType: z.string().min(1),
  /** Size in bytes; null until the upload is known. */
  size: z.number().int().nonnegative().nullable(),
  /** Lowercase-hex SHA-256 of the bytes; null until known. */
  sha256: ArtifactSha256Schema.nullable(),
  /** `run:<id>` | `user:<tokenId>` — {@link artifactCreator}. */
  createdBy: z.string().min(1),
  visibility: ArtifactVisibilitySchema,
  /** When the reaper deletes the object + row; null = never expires. */
  expiresAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type Artifact = z.infer<typeof ArtifactSchema>;
